import { useState } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { Crown, Search, Trash2, UserPlus } from "lucide-react";
import { Button, PageHeading, Spinner } from "@nu-assist/ui";
import { useMe } from "@nu-assist/shared-fe";
import { fetchAdmins, addAdmin, removeAdmin, fetchPeople, transferOwnership } from "./api";
import type { AdminUser } from "./types";

// ---------------------------------------------------------------------------
// Add admin dialog — searchable list of existing staff
// ---------------------------------------------------------------------------

function AddAdminDialog({
  existingAdminEmails,
  onClose,
}: {
  existingAdminEmails: Set<string>;
  onClose: () => void;
}) {
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: people, isLoading } = useQuery({
    queryKey: ["admin-people"],
    queryFn: fetchPeople,
    staleTime: 60_000,
  });

  const candidates = (people ?? [])
    .filter((p) => !existingAdminEmails.has(p.email.toLowerCase()))
    .filter((p) => {
      if (!filter) return true;
      const q = filter.toLowerCase();
      return (
        p.email.toLowerCase().includes(q) ||
        (p.display_name ?? "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) =>
      (a.display_name ?? a.email).localeCompare(b.display_name ?? b.email)
    );

  const mutation = useMutation({
    mutationFn: () => addAdmin(selected!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-admins"] });
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" aria-hidden="true" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add admin"
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div className="bg-bg border border-border rounded-lg shadow-xl w-full max-w-md flex flex-col max-h-[75vh]">
          {/* Header */}
          <div className="px-5 pt-5 pb-3 border-b border-border shrink-0">
            <h2 className="text-h2 font-semibold text-text mb-3">Add admin</h2>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted pointer-events-none" />
              <input
                autoFocus
                type="text"
                placeholder="Filter by name or email…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 border border-border rounded text-body bg-bg text-text focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center gap-2 px-5 py-6">
                <Spinner size="sm" />
                <span className="text-body text-text-secondary">Loading staff…</span>
              </div>
            ) : candidates.length === 0 ? (
              <p className="px-5 py-6 text-body text-text-secondary text-center">
                {filter ? "No staff match this filter." : "All staff are already admins."}
              </p>
            ) : (
              candidates.map((p) => {
                const isSelected = selected === p.email;
                return (
                  <button
                    key={p.email}
                    type="button"
                    onClick={() => setSelected(isSelected ? null : p.email)}
                    className={[
                      "w-full text-left flex items-center gap-3 px-5 py-3 border-b border-border last:border-0 transition-colors",
                      isSelected
                        ? "bg-accent/10"
                        : "hover:bg-surface-hover",
                    ].join(" ")}
                  >
                    <div
                      className={[
                        "w-4 h-4 rounded-full border-2 shrink-0 transition-colors",
                        isSelected
                          ? "border-accent bg-accent"
                          : "border-border bg-bg",
                      ].join(" ")}
                    />
                    <div className="min-w-0">
                      <p className="text-body font-medium text-text truncate">{p.email}</p>
                      {p.role && (
                        <p className="text-label text-text-secondary capitalize">{p.role}</p>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-4 border-t border-border shrink-0 flex flex-col gap-2">
            {error && <p className="text-label text-error">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={!selected || mutation.isPending}
                onClick={() => mutation.mutate()}
              >
                {mutation.isPending ? "Adding…" : "Add admin"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Remove confirmation dialog
// ---------------------------------------------------------------------------

function RemoveAdminDialog({
  admin,
  onClose,
}: {
  admin: AdminUser;
  onClose: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => removeAdmin(admin.email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-admins"] });
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" aria-hidden="true" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Remove admin"
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div className="bg-bg border border-border rounded-lg shadow-xl w-full max-w-sm p-5 flex flex-col gap-4">
          <h2 className="text-h2 font-semibold text-text">Remove admin</h2>
          <p className="text-body text-text-secondary">
            Remove <span className="font-medium text-text">{admin.email}</span> from the admin
            allowlist? They will lose access to the Staff Administration Console immediately.
          </p>
          {error && <p className="text-label text-error">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {mutation.isPending ? "Removing…" : "Remove"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Transfer ownership dialog
// ---------------------------------------------------------------------------

function TransferOwnershipDialog({
  fromEmail,
  admins,
  onClose,
}: {
  fromEmail: string;
  admins: AdminUser[];
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const candidates = admins.filter((a) => a.email.toLowerCase() !== fromEmail.toLowerCase());

  const mutation = useMutation({
    mutationFn: () => transferOwnership(fromEmail, selected!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-admins"] });
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" aria-hidden="true" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Transfer ownership"
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div className="bg-bg border border-border rounded-lg shadow-xl w-full max-w-md flex flex-col max-h-[75vh]">
          <div className="px-5 pt-5 pb-3 border-b border-border shrink-0">
            <h2 className="text-h2 font-semibold text-text mb-1">Transfer ownership</h2>
            <p className="text-body text-text-secondary">
              Select an admin to become the new workspace owner. You will lose owner privileges.
            </p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {candidates.length === 0 ? (
              <p className="px-5 py-6 text-body text-text-secondary text-center">
                No other admins to transfer to.
              </p>
            ) : (
              candidates.map((a) => {
                const isSelected = selected === a.email;
                return (
                  <button
                    key={a.email}
                    type="button"
                    onClick={() => setSelected(isSelected ? null : a.email)}
                    className={[
                      "w-full text-left flex items-center gap-3 px-5 py-3 border-b border-border last:border-0 transition-colors",
                      isSelected ? "bg-accent/10" : "hover:bg-surface-hover",
                    ].join(" ")}
                  >
                    <div
                      className={[
                        "w-4 h-4 rounded-full border-2 shrink-0 transition-colors",
                        isSelected ? "border-accent bg-accent" : "border-border bg-bg",
                      ].join(" ")}
                    />
                    <p className="text-body font-medium text-text truncate">{a.email}</p>
                  </button>
                );
              })
            )}
          </div>
          <div className="px-5 py-4 border-t border-border shrink-0 flex flex-col gap-2">
            {error && <p className="text-label text-error">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
              <Button
                type="button"
                variant="primary"
                disabled={!selected || mutation.isPending}
                onClick={() => mutation.mutate()}
              >
                {mutation.isPending ? "Transferring…" : "Transfer ownership"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AdminsPage() {
  const [showAdd, setShowAdd] = useState(false);
  const [removing, setRemoving] = useState<AdminUser | null>(null);
  const [showTransfer, setShowTransfer] = useState(false);
  const me = useMe();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin-admins"],
    queryFn: fetchAdmins,
    staleTime: 60_000,
    retry: false,
  });

  const admins = data ?? [];
  const currentEmail = me.status === "authenticated" ? me.data.email.toLowerCase() : "";
  const currentUserIsOwner = admins.some(
    (a) => a.email.toLowerCase() === currentEmail && a.is_owner,
  );

  return (
    <div className="min-h-full px-6 py-6 flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <PageHeading
          title="Admins"
          description="Users with access to the Staff Administration Console."
        />
        <div className="shrink-0 mt-1 flex gap-2">
          {currentUserIsOwner && (
            <Button variant="secondary" onClick={() => setShowTransfer(true)}>
              <Crown className="w-3.5 h-3.5" aria-hidden="true" />
              Transfer ownership
            </Button>
          )}
          <Button variant="primary" onClick={() => setShowAdd(true)}>
            <UserPlus className="w-3.5 h-3.5" aria-hidden="true" />
            Add admin
          </Button>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center gap-2 py-8">
          <Spinner size="sm" />
          <span className="text-body text-text-secondary">Loading admins…</span>
        </div>
      )}

      {/* Error */}
      {error && !isLoading && (
        <p className="text-body text-error py-4">
          Failed to load admins.{" "}
          <button type="button" className="underline" onClick={() => refetch()}>
            Retry
          </button>
        </p>
      )}

      {/* Table */}
      {!isLoading && !error && (
        <div className="overflow-x-auto rounded-md border border-border bg-table-surface">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-border text-left">
                {(["Email", "Granted by", "Granted at", "Actions"] as const).map((col) => (
                  <th key={col} scope="col" className="text-label font-semibold text-text-secondary">
                    <span className="block px-3 py-2">{col}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr
                  key={a.email}
                  className="border-b border-border hover:bg-surface-hover transition-colors last:border-0"
                >
                  <td className="py-2.5 px-3 text-body text-text font-medium">
                    <span className="flex items-center gap-1.5">
                      {a.email}
                      {a.is_owner && (
                        <span title="Workspace owner" className="inline-flex items-center">
                          <Crown className="w-3.5 h-3.5 text-amber-500" aria-label="Workspace owner" />
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-body text-text-secondary">{a.granted_by}</td>
                  <td className="py-2.5 px-3 text-body text-text-secondary">
                    {new Date(a.granted_at).toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="py-2.5 px-3">
                    <button
                      type="button"
                      title={`Remove ${a.email}`}
                      onClick={() => setRemoving(a)}
                      className="inline-flex items-center justify-center w-7 h-7 rounded text-text-secondary hover:text-error hover:bg-red-500/10 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
              {admins.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-body text-text-secondary">
                    No admins found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <AddAdminDialog
          existingAdminEmails={new Set(admins.map((a) => a.email.toLowerCase()))}
          onClose={() => setShowAdd(false)}
        />
      )}
      {removing && <RemoveAdminDialog admin={removing} onClose={() => setRemoving(null)} />}
      {showTransfer && currentEmail && (
        <TransferOwnershipDialog
          fromEmail={currentEmail}
          admins={admins}
          onClose={() => setShowTransfer(false)}
        />
      )}
    </div>
  );
}
